import { useEffect, useMemo, useState } from 'react';
import { X, Check, Layers } from 'lucide-react';
import { SlideDrawer, type SlideDrawerZTier } from '@/components/ui/slide-drawer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { capabilityColors, getCapabilityColors } from '@/lib/capability-colors';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import type { CapabilityVisibility } from '@/types/capability-types';
import { normalizeCapabilityVisibility } from '@/lib/capability-visibility';

export interface CapabilityPickerItem {
    name: string;
    description?: string | null;
    tags?: string[];
    fqcn?: string;
    aliases?: string[];
    provider?: string;
    app_key?: string;
    visibility?: string;
    created_by?: string;
    organization_id?: string;
    status?: string;
    type?: string;
    access_source?: string;
}

type SlotType = 'model' | 'prompt' | 'tool' | 'skill' | 'memory' | 'schema';

export type VisibilityScope = CapabilityVisibility | 'all';

const VISIBILITY_SCOPE_OPTIONS: { key: VisibilityScope; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'user', label: 'User' },
    { key: 'application', label: 'App' },
    { key: 'organization', label: 'Organization' },
    { key: 'account', label: 'Account' },
    { key: 'global', label: 'Global' },
];

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
];

const LEGACY_DEFAULT_DRAWER_SIZE = 'w-[65%] h-[90vh]';

function resolveSlideDrawerZTier(params: {
    overlayZClassName?: string;
    drawerZClassName?: string;
}): SlideDrawerZTier {
    const drawerZ = params.drawerZClassName ?? 'z-201';
    const overlayZ = params.overlayZClassName ?? 'z-200';
    if (drawerZ.includes('401') || overlayZ.includes('400')) return 'top';
    if (drawerZ.includes('301') || overlayZ.includes('300')) return 'stacked';
    return 'default';
}

function resolveSlideDrawerSize(drawerSizeClassName: string): {
    width?: string;
    className?: string;
} {
    if (
        !drawerSizeClassName ||
        drawerSizeClassName === LEGACY_DEFAULT_DRAWER_SIZE
    ) {
        return {};
    }
    const parts = drawerSizeClassName.split(/\s+/).filter(Boolean);
    const width = parts.find((p) => /^w-/.test(p));
    const className = parts
        .filter(
            (p) =>
                p !== width &&
                !/^z-/.test(p) &&
                !/^h-\[/.test(p),
        )
        .join(' ');
    return {
        width: width ?? 'w-[60%]',
        className: className || undefined,
    };
}

interface CapabilityPickerDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    slotType: SlotType;
    items: CapabilityPickerItem[];
    selectedRefs: string[];
    onSelect: (name: string) => void;
    loading?: boolean;
    appKey?: string;
    currentUserId?: string;
    currentOrgId?: string;
    overlayZClassName?: string;
    drawerZClassName?: string;
    drawerSizeClassName?: string;
}

export function CapabilityPickerDrawer({
    isOpen,
    onClose,
    slotType,
    items,
    selectedRefs,
    onSelect,
    loading = false,
    appKey: _appKey = '',
    currentUserId: _currentUserId = '',
    currentOrgId: _currentOrgId = '',
    overlayZClassName = 'z-200',
    drawerZClassName = 'z-201',
    drawerSizeClassName = 'w-[65%] h-[90vh]',
}: CapabilityPickerDrawerProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<string>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>('all');
    const [typeFilter, setTypeFilter] = useState<string | null>(null);

    const showTypeFilter = slotType === 'tool';

    const availableTypes = useMemo(() => {
        if (!showTypeFilter) return [];
        const types = new Set<string>();
        for (const item of items) {
            if (item.type) types.add(item.type);
        }
        return Array.from(types).sort();
    }, [items, showTypeFilter]);

    const colors = capabilityColors[slotType];
    const Icon = colors.icon;
    const typeLabel =
        slotType === 'tool' ? 'Capability' : slotType === 'memory' ? 'Memory' : slotType.charAt(0).toUpperCase() + slotType.slice(1);

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const item of items) {
            if (item.tags) {
                for (const t of item.tags) tagSet.add(t);
            }
        }
        return Array.from(tagSet).sort();
    }, [items]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredItems = useMemo(() => {
        let list = items.filter(item => {
            if (item.status?.toLowerCase() !== 'active') return false;
            if (typeFilter && item.type !== typeFilter) return false;
            if (
                visibilityScope !== 'all' &&
                item.access_source !== 'entitled' &&
                normalizeCapabilityVisibility(item.visibility) !== visibilityScope
            ) {
                return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                if (
                    !item.name.toLowerCase().includes(q) &&
                    !(item.description?.toLowerCase().includes(q) ?? false) &&
                    !(item.fqcn?.toLowerCase().includes(q) ?? false)
                ) {
                    return false;
                }
            }
            if (selectedTags.size > 0 && item.tags) {
                if (!item.tags.some(t => selectedTags.has(t))) return false;
            } else if (selectedTags.size > 0 && !item.tags?.length) {
                return false;
            }
            return true;
        });

        list = [...list].sort((a, b) => {
            let r: number;
            if (sortField === 'name') {
                r = a.name.localeCompare(b.name);
            } else {
                r = (a.description ?? '').localeCompare(b.description ?? '');
            }
            return sortDirection === 'asc' ? r : -r;
        });

        return list;
    }, [items, searchQuery, selectedTags, sortField, sortDirection, visibilityScope, typeFilter]);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSelectedTags(new Set());
            setVisibilityScope('all');
            setTypeFilter(null);
        }
    }, [isOpen]);

    const selectedSet = useMemo(() => new Set(selectedRefs), [selectedRefs]);

    const isItemSelected = (item: CapabilityPickerItem) => {
        const key = item.fqcn ?? item.name;
        return selectedSet.has(key) || selectedSet.has(item.name);
    };

    const zTier = resolveSlideDrawerZTier({ overlayZClassName, drawerZClassName });
    const { width: drawerWidth, className: drawerPanelClassName } =
        resolveSlideDrawerSize(drawerSizeClassName);

    return (
        <SlideDrawer
            isOpen={isOpen}
            onClose={onClose}
            zTier={zTier}
            width={drawerWidth}
            className={drawerPanelClassName}
        >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className={cn(
                                    'flex h-10 w-10 items-center justify-center rounded-lg',
                                    showTypeFilter ? 'bg-slate-100 text-slate-600' : `${colors.bgLight} ${colors.text}`,
                                )}>
                                    {showTypeFilter ? <Layers size={20} /> : <Icon size={20} />}
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        Select {typeLabel}
                                    </h2>
                                    <p className="text-sm text-slate-500">
                                        {showTypeFilter
                                            ? 'Choose any capability to add as a tool for this agent'
                                            : `Choose a ${slotType} capability for this agent`}
                                    </p>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                                <X size={16} />
                            </Button>
                        </div>

                        <CapabilityListPanel<CapabilityPickerItem>
                                items={filteredItems}
                                sortOptions={SORT_OPTIONS}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                searchPlaceholder={showTypeFilter ? 'Search capabilities by name or description...' : `Search ${slotType}s by name or description...`}
                                allTags={allTags}
                                selectedTags={selectedTags}
                                extraToolbar={
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                                            {VISIBILITY_SCOPE_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    onClick={() => setVisibilityScope(opt.key)}
                                                    className={cn(
                                                        'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                                        visibilityScope === opt.key
                                                            ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                                                            : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
                                                    )}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        {showTypeFilter && availableTypes.length > 1 && (
                                            <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setTypeFilter(null)}
                                                    className={cn(
                                                        'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                                        typeFilter === null
                                                            ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                                                            : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
                                                    )}
                                                >
                                                    All
                                                </button>
                                                {availableTypes.map(t => {
                                                    const tc = getCapabilityColors(t);
                                                    return (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => setTypeFilter(t)}
                                                            className={cn(
                                                                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                                                typeFilter === t
                                                                    ? `${tc.bgLight} ${tc.text} shadow-sm`
                                                                    : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
                                                            )}
                                                        >
                                                            <tc.icon size={12} />
                                                            {t}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                }
                                onToggleTag={tag => {
                                    setSelectedTags(prev => {
                                        const next = new Set(prev);
                                        if (next.has(tag)) next.delete(tag);
                                        else next.add(tag);
                                        return next;
                                    });
                                }}
                                onClearAllTags={() => setSelectedTags(new Set())}
                                onSelectAllTags={() => setSelectedTags(new Set(allTags))}
                                isLoading={loading}
                                loadingMessage={`Loading ${slotType}s...`}
                                emptyMessage={`No ${slotType}s found.`}
                                toolbarClassName="px-12 py-4 border-b border-slate-100 shrink-0"
                                listControlsVariant="solid"
                                contentClassName="flex-1 overflow-y-auto px-24 py-6"
                                gridClassName="grid grid-cols-3 gap-3"
                                renderCard={item => {
                                    const selected = isItemSelected(item);
                                    const itemColors = (showTypeFilter && item.type)
                                        ? getCapabilityColors(item.type)
                                        : colors;
                                    const ItemIcon = itemColors.icon;
                                    const itemTypeLabel = item.type ?? slotType;
                                    return (
                                        <Card
                                            key={item.fqcn ?? item.name}
                                            className={cn(
                                                'relative border-2 p-3 transition-all duration-200 cursor-pointer min-h-[88px]',
                                                'border-l-4',
                                                selected
                                                    ? 'border-green-500 bg-green-50/50 ring-2 ring-green-200'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md',
                                            )}
                                            style={{
                                                borderLeftColor: selected ? undefined : itemColors.hex,
                                            }}
                                            onClick={() => {
                                                onSelect(item.fqcn ?? item.name);
                                                onClose();
                                            }}
                                        >
                                            <div className="flex flex-row items-start gap-3">
                                                <div className={cn(
                                                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                                    itemColors.bgLight, itemColors.text,
                                                )}>
                                                    <ItemIcon size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={cn('text-[10px] font-medium uppercase tracking-wide', itemColors.text)}>
                                                            {itemTypeLabel}
                                                        </span>
                                                        {item.access_source === 'entitled' && (
                                                            <Badge variant="secondary" className="h-4 px-1.5 text-[9px] font-medium">
                                                                Subscribed
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className="block text-sm font-bold truncate text-slate-900">
                                                        {item.name}
                                                    </span>
                                                    {item.provider && (
                                                        <span className={cn('block text-[10px] truncate', itemColors.text)}>
                                                            {item.provider}
                                                        </span>
                                                    )}
                                                    {item.description && (
                                                        <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight block mt-0.5">
                                                            {item.description}
                                                        </span>
                                                    )}
                                                </div>
                                                {selected && (
                                                    <Check size={16} className="shrink-0 text-green-600 mt-1" />
                                                )}
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
        </SlideDrawer>
    );
}
